import React, { useState } from 'react';
import { X, Check, Zap, Crown, ExternalLink } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { externalLinkProps, openExternal } from '../utils/openExternal';
import toast from 'react-hot-toast';
import './UpgradeModal.css';

const PUBLIC_SITE = 'https://molehill.wolftechnologies.co.uk';
const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

// Guideline 3.1.2(c): every plan must state its TITLE, LENGTH, PRICE and — where
// the billing period is longer than a month — the PRICE PER UNIT, on the
// purchase screen itself. `title`/`lengthLabel`/`unitLabel` exist for that
// disclosure; do not collapse them into the short `name` used on the card.
const PLANS = [
  {
    id: 'monthly',
    iapId: 'premium_monthly',
    name: 'Monthly',
    title: 'Molehill Premium — Monthly',
    price: '£4.99',
    period: 'per month',
    lengthLabel: '1 month',
    // Monthly IS the unit price, so no separate per-unit line is needed.
    unitMonths: 1,
    badge: null,
    availableOnIOS: true,
  },
  {
    id: 'annual',
    iapId: 'premium_yearly',
    name: 'Annual',
    title: 'Molehill Premium — Annual',
    price: '£39.99',
    period: 'per year',
    lengthLabel: '12 months',
    unitMonths: 12,
    badge: 'Save 33%',
    availableOnIOS: true,
  },
  {
    id: 'lifetime',
    iapId: null,
    name: 'Lifetime',
    title: 'Molehill Premium — Lifetime',
    price: '£149.99',
    period: 'one-time',
    lengthLabel: 'One-time purchase (not a subscription)',
    unitMonths: null,
    badge: 'Best Value',
    availableOnIOS: false, // Apple IAP doesn't support lifetime subscriptions natively
  },
];

const FEATURES = [
  'Unlimited AI brain dumps',
  'Unlimited projects',
  'Areas of responsibility',
  'Repeating tasks',
  'Advanced search',
  'Priority support',
];

/**
 * Split a localized store price ("£39.99", "39,99 €", "¥6,000") into its
 * currency prefix/suffix and numeric value so a per-month equivalent can be
 * rendered in the same currency and layout the App Store returned.
 * Returns null when the string can't be parsed — callers then omit the
 * per-unit line rather than printing a wrong number.
 */
function splitPrice(display) {
  if (!display) return null;
  const str = String(display);
  // Grab the number, then hand any trailing spaces/separators back to the
  // suffix so "39,99 €" keeps its space and "€" stays a suffix.
  const match = str.match(/\d[\d.,\s ]*/);
  if (!match) return null;
  const numeric = match[0].replace(/[\s .,]+$/, '');
  if (!numeric) return null;

  const prefix = str.slice(0, match.index);
  const suffix = str.slice(match.index + numeric.length);
  const cleaned = numeric.replace(/[\s ]/g, '');

  const sep = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
  const trailing = sep === -1 ? 0 : cleaned.length - sep - 1;
  // 1 or 2 digits after the last separator = decimal separator;
  // anything else (e.g. "6,000") is a thousands separator.
  const isDecimal = trailing === 1 || trailing === 2;
  const sepChar = isDecimal ? cleaned[sep] : '.';

  const value = isDecimal
    ? Number(`${cleaned.slice(0, sep).replace(/[.,]/g, '')}.${cleaned.slice(sep + 1)}`)
    : Number(cleaned.replace(/[.,]/g, ''));

  if (!Number.isFinite(value) || value <= 0) return null;
  return { prefix, suffix, value, decimals: isDecimal ? 2 : 0, sepChar };
}

/** "£39.99" + 12 months -> "£3.33" (the per-unit price Guideline 3.1.2(c) asks for). */
function perMonthPrice(display, unitMonths) {
  if (!unitMonths || unitMonths <= 1) return null;
  const parsed = splitPrice(display);
  if (!parsed) return null;
  const perMonth = (parsed.value / unitMonths).toFixed(parsed.decimals);
  // Render in the same decimal separator the store used ("3,33 €", not "3.33 €").
  return `${parsed.prefix}${perMonth.replace('.', parsed.sepChar)}${parsed.suffix}`;
}

export default function UpgradeModal({ onClose, contextMessage }) {
  const {
    profile,
    status,
    isPremium,
    isTrialing,
    startApplePurchase,
    getPaymentPlatform,
    getApplePrice,
  } = useSubscription();
  const { isAuthenticated, openAuthModal } = useAuth();
  const platform = getPaymentPlatform();

  // A paid, non-trial subscriber can't buy again — but Guideline 3.1.2(c)
  // requires the subscription's title, length, price and legal links to be
  // reachable IN the app, and this modal is the only screen that carries them.
  // So the whole plan list still renders; only the CTA changes.
  const isSubscribed = isPremium && !isTrialing;
  const currentPlanId = isSubscribed
    ? profile?.subscriptionPlan || status?.subscriptionPlan || null
    : null;

  const [selectedPlan, setSelectedPlan] = useState(currentPlanId || 'annual');
  const [isLoading, setIsLoading] = useState(false);

  // Filter plans: hide lifetime on iOS (not available via Apple IAP)
  const availablePlans = platform === 'apple'
    ? PLANS.filter((p) => p.availableOnIOS)
    : PLANS;

  // Get the display price for a plan (use Apple store price on iOS if available)
  const getDisplayPrice = (plan) => {
    if (platform === 'apple' && plan.iapId) {
      const applePrice = getApplePrice(plan.iapId);
      if (applePrice) return applePrice;
    }
    return plan.price;
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      toast('Sign in to start your Premium subscription', { icon: '🔑' });
      onClose();
      openAuthModal();
      return;
    }

    if (platform !== 'apple') {
      // Apple IAP is the only payment path — direct web users to the iOS app.
      toast('Premium is available in the Molehill iOS app.');
      return;
    }

    setIsLoading(true);
    try {
      const plan = PLANS.find((p) => p.id === selectedPlan);
      if (!plan?.iapId) {
        toast.error('This plan is not available on iOS');
        return;
      }
      await startApplePurchase(plan.iapId);
      // Purchase flow continues in the CdvPurchase verified handler
      toast.success('Purchase started! Processing...');
      onClose();
    } catch (err) {
      if (err.message) {
        toast.error(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selected = PLANS.find((p) => p.id === selectedPlan) || availablePlans[0];
  const selectedPrice = selected ? getDisplayPrice(selected) : null;
  const selectedPerMonth = selected ? perMonthPrice(selectedPrice, selected.unitMonths) : null;

  return (
    <div className="upgrade-modal-backdrop" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className="upgrade-modal-header">
          <div className="upgrade-modal-icon">
            <Crown size={24} />
          </div>
          <h2>{isSubscribed ? 'Molehill Premium' : 'Upgrade to Premium'}</h2>
          {isSubscribed ? (
            <p className="upgrade-modal-context">
              You have an active Premium subscription. Your plan details are below.
            </p>
          ) : (
            contextMessage && <p className="upgrade-modal-context">{contextMessage}</p>
          )}
        </div>

        <div className="upgrade-plans">
          {availablePlans.map((plan) => {
            const displayPrice = getDisplayPrice(plan);
            const perMonth = perMonthPrice(displayPrice, plan.unitMonths);
            return (
              <button
                key={plan.id}
                type="button"
                className={`upgrade-plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
                aria-pressed={selectedPlan === plan.id}
              >
                {plan.badge && !isSubscribed && (
                  <span className="plan-badge">{plan.badge}</span>
                )}
                {currentPlanId === plan.id && (
                  <span className="plan-badge plan-badge--current">Current</span>
                )}
                <span className="plan-name">{plan.name}</span>
                <span className="plan-price">{displayPrice}</span>
                <span className="plan-period">{plan.period}</span>
                <span className="plan-length">{plan.lengthLabel}</span>
                {perMonth && (
                  <span className="plan-unit-price">{perMonth} / month</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Guideline 3.1.2(c) disclosure for the selected plan: title, length,
            price and price per unit, all on the purchase screen itself. */}
        {selected && (
          <div className="upgrade-terms-summary">
            <p className="upgrade-terms-title">{selected.title}</p>
            <p className="upgrade-terms-line">
              <strong>{selectedPrice}</strong> {selected.period}
              {selected.unitMonths ? ` · ${selected.lengthLabel}` : ''}
              {selectedPerMonth ? ` · equivalent to ${selectedPerMonth} per month` : ''}
            </p>
            {selected.iapId && (
              <p className="upgrade-terms-line upgrade-terms-line--muted">
                Auto-renewing subscription. Renews every {selected.lengthLabel} at{' '}
                {selectedPrice} until cancelled.
              </p>
            )}
          </div>
        )}

        <ul className="upgrade-features">
          {FEATURES.map((feature) => (
            <li key={feature}>
              <Check size={16} className="feature-check" />
              {feature}
            </li>
          ))}
        </ul>

        {isSubscribed ? (
          <button
            type="button"
            className="upgrade-cta"
            onClick={() => openExternal(APPLE_SUBSCRIPTIONS_URL)}
          >
            <ExternalLink size={16} />
            Manage Subscription
          </button>
        ) : (
          <button
            type="button"
            className="upgrade-cta"
            onClick={handleUpgrade}
            disabled={isLoading}
          >
            <Zap size={16} />
            {isLoading
              ? (platform === 'apple' ? 'Processing...' : 'Redirecting...')
              : 'Start Premium'}
          </button>
        )}

        <p className="upgrade-disclaimer">
          {platform === 'apple'
            ? 'Payment will be charged to your Apple ID account at confirmation of purchase. The subscription automatically renews at the price above unless it is cancelled at least 24 hours before the end of the current period. Your account will be charged for renewal within 24 hours prior to the end of the current period. Manage or cancel your subscription in your App Store account settings after purchase.'
            : 'Premium subscriptions are available in the Molehill iOS app.'}
        </p>
        <p className="upgrade-legal-links">
          <a {...externalLinkProps(`${PUBLIC_SITE}/terms.html`)}>Terms of Use (EULA)</a>
          {' · '}
          <a {...externalLinkProps(`${PUBLIC_SITE}/privacy.html`)}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
